import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Archive, MapPin, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { fmtDT } from "@/lib/format";

const STATUSES = ["active", "inactive", "blocked", "archived"];
const STATUS_CLS = { active: "bg-signal-green/15 text-signal-green", inactive: "bg-muted text-muted-foreground", blocked: "bg-signal-red/15 text-signal-red", archived: "bg-signal-amber/15 text-signal-amber" };
const EMPTY = { name: "", code: "", type: "", address: "", city: "", state: "", country: "India", pincode: "", contact_person: "", contact_number: "", status: "active", remarks: "" };
const EMPTY_LOC = { zone: "", aisle: "", rack: "", level: "", shelf: "", bin: "", sku: "", quantity: 0 };

const DEP_LABELS = { locations: "Locations", locations_with_stock: "Locations with stock", stock_records: "Stock records", reservations: "Active reservations", inward: "Inward records", orders: "Orders", tasks: "Active tasks", stock_movements: "Stock movements" };

function F({ label, children, testId }) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function Warehouses() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const migrateTargets = useMemo(() => rows.filter((r) => r.warehouse_id !== deleteTarget?.warehouse_id && r.status === "active"), [rows, deleteTarget]);
  const [deps, setDeps] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [migrateTo, setMigrateTo] = useState("");
  const [locFor, setLocFor] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locForm, setLocForm] = useState(EMPTY_LOC);
  const [statusReason, setStatusReason] = useState({});

  const load = useCallback(() => api.get("/warehouses").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const loadLocations = useCallback(async (w) => {
    setLocFor(w);
    const r = await api.get(`/warehouses/${w.warehouse_id}/locations`);
    setLocations(r.data);
  }, []);

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) return toast.error("Name and code are required");
    try {
      if (editId) {
        await api.put(`/warehouses/${editId}`, form);
        toast.success("Warehouse updated — edit recorded in audit trail");
      } else {
        await api.post("/warehouses", form);
        toast.success(`Warehouse ${form.code.toUpperCase()} created`);
      }
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    }
  };

  const openEdit = (w) => {
    setEditId(w.warehouse_id);
    const f = { ...EMPTY };
    for (const k of Object.keys(EMPTY)) f[k] = w[k] ?? (k === "country" ? "India" : "");
    setForm(f);
    setOpen(true);
  };

  const changeStatus = async (w, status) => {
    const reason = statusReason[w.warehouse_id] || "";
    try {
      await api.post(`/warehouses/${w.warehouse_id}/status`, { status, reason: reason || null });
      toast.success(`${w.name} → ${status}`);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const startDelete = async (w) => {
    try {
      const r = await api.get(`/warehouses/${w.warehouse_id}/dependencies`);
      setDeps(r.data);
      setDeleteTarget(w);
      setConfirmText("");
      setMigrateTo("");
    } catch (e) {
      toast.error("Could not check dependencies");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/warehouses/${deleteTarget.warehouse_id}?reason=${encodeURIComponent("Admin deleted after dependency check")}`);
      toast.success(`Warehouse ${deleteTarget.name} permanently deleted`);
      setDeleteTarget(null);
      setDeps(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Delete failed");
    }
  };

  const doArchive = async () => {
    try {
      await api.post(`/warehouses/${deleteTarget.warehouse_id}/status`, { status: "archived", reason: "Archived instead of delete (linked history preserved)" });
      toast.success(`${deleteTarget.name} archived — history preserved`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const doMigrate = async () => {
    if (!migrateTo) return toast.error("Choose a target warehouse");
    try {
      const r = await api.post(`/warehouses/${deleteTarget.warehouse_id}/migrate`, { target_warehouse_id: migrateTo });
      toast.success(`Data migrated (${JSON.stringify(r.data.moved)}) — source archived`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Migration failed");
    }
  };

  const addLocation = async () => {
    if (!locForm.zone || !locForm.rack || !locForm.shelf || !locForm.bin) return toast.error("Zone, rack, shelf and bin are required");
    try {
      await api.post(`/warehouses/${locFor.warehouse_id}/locations`, { ...locForm, sku: locForm.sku || null, quantity: parseInt(locForm.quantity) || 0 });
      toast.success(`Location ${locForm.bin} added`);
      setLocForm(EMPTY_LOC);
      loadLocations(locFor);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const delLocation = async (bin) => {
    try {
      await api.delete(`/warehouses/${locFor.warehouse_id}/locations/${bin}`);
      toast.success(`Location ${bin} deleted`);
      loadLocations(locFor);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="warehouses-page">
      <SectionTitle right={isAdmin && (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="create-warehouse-button"><Plus className="w-4 h-4 mr-1" /> Create Warehouse</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="warehouse-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">{editId ? "Edit Warehouse" : "Create Warehouse"}</DialogTitle>
              <DialogDescription className="sr-only">Warehouse master record</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <F label="Warehouse Name *"><Input data-testid="wh-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main Warehouse" /></F>
              <F label="Warehouse Code *"><Input data-testid="wh-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MAIN" className="font-mono" /></F>
              <F label="Warehouse Type">
                <Select value={form.type || "none"} onValueChange={(v) => setForm({ ...form, type: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="wh-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Select —</SelectItem>
                    {["Finished Goods", "Raw Material", "In-Transit", "Quality Hold", "General"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="wh-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Address"><Input data-testid="wh-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></F>
              <F label="City"><Input data-testid="wh-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></F>
              <F label="State"><Input data-testid="wh-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></F>
              <F label="Country"><Input data-testid="wh-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></F>
              <F label="Pincode"><Input data-testid="wh-pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></F>
              <F label="Contact Person"><Input data-testid="wh-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></F>
              <F label="Contact Number"><Input data-testid="wh-phone" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} /></F>
              <F label="Remarks"><Input data-testid="wh-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></F>
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 mt-3" onClick={save} data-testid="wh-save-button">{editId ? "Save Changes" : "Create Warehouse"}</Button>
          </DialogContent>
        </Dialog>
      )}>Warehouse Master</SectionTitle>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="warehouses-table">
          <thead><tr className="border-b border-border text-left">
            {["Code", "Warehouse Name", "Type", "City / State", "Contact", "Status", "Created", ""].map((h) => (
              <th key={h} className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.warehouse_id} className="border-b border-border/50" data-testid={`warehouse-row-${w.warehouse_id}`}>
                <td className="px-3 py-2.5 font-mono text-xs text-primary font-bold">{w.code}</td>
                <td className="px-3 py-2.5 font-medium">{w.name}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{w.type || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{[w.city, w.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{[w.contact_person, w.contact_number].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-3 py-2.5">
                  {isAdmin ? (
                    <Select value={w.status} onValueChange={(v) => changeStatus(w, v)}>
                      <SelectTrigger className={`w-32 h-8 text-[10px] font-mono uppercase tracking-widest ${STATUS_CLS[w.status]}`} data-testid={`wh-status-${w.warehouse_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`wh-status-${s}-${w.warehouse_id}`}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${STATUS_CLS[w.status]}`}>{w.status}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{fmtDT(w.created_at)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" title="Locations" onClick={() => loadLocations(w)} data-testid={`wh-locations-${w.warehouse_id}`}><MapPin className="w-3.5 h-3.5" /></Button>
                    {isAdmin && (
                      <>
                        <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(w)} data-testid={`wh-edit-${w.warehouse_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" title="Archive" onClick={() => changeStatus(w, "archived")} data-testid={`wh-archive-${w.warehouse_id}`}><Archive className="w-3.5 h-3.5 text-signal-amber" /></Button>
                        <Button size="sm" variant="ghost" title="Delete" onClick={() => startDelete(w)} data-testid={`wh-delete-${w.warehouse_id}`}><Trash2 className="w-3.5 h-3.5 text-signal-red" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No warehouses yet — Admin creates the first one here.</td></tr>}
          </tbody>
        </table>
      </div>

      {locFor && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="locations-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold uppercase tracking-wide">Locations — {locFor.name} <span className="text-[10px] font-mono text-muted-foreground">(status: {locFor.status})</span></p>
            <Button size="sm" variant="ghost" onClick={() => setLocFor(null)}>Close</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="wh-locations-table">
              <thead><tr className="border-b border-border text-left">
                {["Zone", "Aisle", "Rack", "Level", "Shelf", "Bin", "Code", "SKU", "Qty", ""].map((h) => <th key={h} className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>)}
              </tr></thead>
              <tbody>
                {locations.map((l) => (
                  <tr key={l.bin} className="border-b border-border/50" data-testid={`loc-row-${l.bin}`}>
                    <td className="px-3 py-2 text-xs">{l.zone}</td>
                    <td className="px-3 py-2 text-xs">{l.aisle || "—"}</td>
                    <td className="px-3 py-2 text-xs">{l.rack}</td>
                    <td className="px-3 py-2 text-xs">{l.level || "—"}</td>
                    <td className="px-3 py-2 text-xs">{l.shelf}</td>
                    <td className="px-3 py-2 font-mono text-xs font-bold">{l.bin}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{l.code || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{l.sku || "—"}</td>
                    <td className="px-3 py-2 metric text-xs">{l.quantity}</td>
                    <td className="px-3 py-2">{isAdmin && l.quantity <= 0 && <Button size="sm" variant="ghost" onClick={() => delLocation(l.bin)} data-testid={`loc-delete-${l.bin}`}><Trash2 className="w-3.5 h-3.5 text-signal-red" /></Button>}</td>
                  </tr>
                ))}
                {locations.length === 0 && <tr><td colSpan={10} className="px-4 py-6 text-center text-sm text-muted-foreground">No locations yet.</td></tr>}
              </tbody>
            </table>
          </div>
          {isAdmin && locFor.status === "active" && (
            <div className="flex gap-2 flex-wrap items-end" data-testid="location-add-form">
              <F label="Zone *"><Input className="w-24 h-8" data-testid="loc-zone" value={locForm.zone} onChange={(e) => setLocForm({ ...locForm, zone: e.target.value })} /></F>
              <F label="Aisle"><Input className="w-20 h-8" data-testid="loc-aisle" value={locForm.aisle} onChange={(e) => setLocForm({ ...locForm, aisle: e.target.value })} /></F>
              <F label="Rack *"><Input className="w-24 h-8" data-testid="loc-rack" value={locForm.rack} onChange={(e) => setLocForm({ ...locForm, rack: e.target.value })} /></F>
              <F label="Level"><Input className="w-20 h-8" data-testid="loc-level" value={locForm.level} onChange={(e) => setLocForm({ ...locForm, level: e.target.value })} /></F>
              <F label="Shelf *"><Input className="w-24 h-8" data-testid="loc-shelf" value={locForm.shelf} onChange={(e) => setLocForm({ ...locForm, shelf: e.target.value })} /></F>
              <F label="Bin *"><Input className="w-28 h-8 font-mono" data-testid="loc-bin" value={locForm.bin} onChange={(e) => setLocForm({ ...locForm, bin: e.target.value.toUpperCase() })} /></F>
              <F label="SKU (optional)"><Input className="w-40 h-8 font-mono" data-testid="loc-sku" value={locForm.sku} onChange={(e) => setLocForm({ ...locForm, sku: e.target.value })} /></F>
              <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground h-8" onClick={addLocation} data-testid="loc-add-button"><Plus className="w-3.5 h-3.5 mr-1" /> Add Location</Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="warehouse-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-signal-red">Delete Warehouse — {deleteTarget?.name}</DialogTitle>
            <DialogDescription className="sr-only">Dependency check before warehouse deletion</DialogDescription>
          </DialogHeader>
          {deps && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2" data-testid="dependency-summary">
                {Object.entries(deps.dependencies).map(([k, v]) => (
                  <div key={k} className="flex justify-between border border-border rounded px-2.5 py-1.5 text-xs">
                    <span className="text-muted-foreground">{DEP_LABELS[k]}</span>
                    <span className={`metric font-bold ${v > 0 ? "text-signal-red" : "text-signal-green"}`} data-testid={`dep-${k}`}>{v}</span>
                  </div>
                ))}
              </div>
              {deps.safe_to_delete ? (
                <div className="space-y-2">
                  <p className="text-sm text-signal-green font-semibold">No linked data — safe for permanent delete. Empty locations will be removed with it.</p>
                  <p className="text-xs text-muted-foreground">Type <span className="font-mono font-bold">DELETE WAREHOUSE</span> to confirm:</p>
                  <Input data-testid="delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="font-mono" />
                  <Button className="w-full bg-signal-red/90 hover:bg-signal-red text-white" disabled={confirmText !== "DELETE WAREHOUSE"} onClick={doDelete} data-testid="delete-confirm-button">Permanently Delete</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-bold text-signal-amber" data-testid="delete-blocked-note">This warehouse has linked historical data. Permanent delete is blocked to protect audit history.</p>
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-signal-amber/20 text-signal-amber hover:bg-signal-amber/30" onClick={doArchive} data-testid="archive-instead-button"><Archive className="w-4 h-4 mr-1" /> Archive Warehouse</Button>
                  </div>
                  <div className="flex gap-2">
                    <Select value={migrateTo || "none"} onValueChange={(v) => setMigrateTo(v === "none" ? "" : v)}>
                      <SelectTrigger className="flex-1" data-testid="migrate-target"><SelectValue placeholder="Migrate data to…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Choose target —</SelectItem>
                        {migrateTargets.map((r) => (
                          <SelectItem key={r.warehouse_id} value={r.warehouse_id}>{r.name} ({r.code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={doMigrate} disabled={!migrateTo} data-testid="migrate-button"><ArrowRightLeft className="w-4 h-4 mr-1" /> Migrate & Archive</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
